import { PublicKey } from '@solana/web3.js';

/**
 * Chain-specific addresses resolve from the process environment at module
 * load, so one build of every service can point at any deployment of the
 * Markets program (devnet, stage, local SVM). Browser and React Native
 * bundles have no such environment; their hosts call
 * `configureChainAddresses` with the active network's addresses instead.
 */
function chainEnv(name: string, fallback: string): string {
  const env = typeof process !== 'undefined' ? process.env : undefined;
  const value = env?.[name]?.trim();
  return value ? value : fallback;
}

/** Environment variable naming the Markets program deployment. */
export const MORFI_PROGRAM_ID_ENV = 'MORFI_PROGRAM_ID';
/** Environment variable naming the cp-swap program deployment paired with it. */
export const MORFI_CP_SWAP_PROGRAM_ID_ENV = 'MORFI_CP_SWAP_PROGRAM_ID';
/** Environment variable naming the quote (USDC) mint the deployment trades in. */
export const MORFI_QUOTE_MINT_ENV = 'MORFI_QUOTE_MINT';
/** Environment variable selecting the opt-in sell-cap Markets program variant (`true`). */
export const MORFI_SELL_CAP_PROGRAM_ENV = 'MORFI_SELL_CAP_PROGRAM';

/** Markets program ID — `MORFI_PROGRAM_ID`, defaulting to the devnet deployment. */
export let MORFI_OPINION_PROGRAM_ID: PublicKey = new PublicKey(
  chainEnv(MORFI_PROGRAM_ID_ENV, 'XuXkWAz3xRktfEjAfFDdkFksYBqFzet8JPkQxZMoRfi'),
);

/**
 * Program ID for the cp-swap (Raydium CPMM fork) program used by graduated
 * markets — `MORFI_CP_SWAP_PROGRAM_ID`, defaulting to the devnet deployment.
 * cp-swap accepts graduation CPIs from a single Markets program, so every
 * Markets deployment pairs with its own cp-swap deployment. Its custom error
 * codes overlap the markets program, so transaction-error parsing
 * disambiguates by this id.
 */
export let CP_SWAP_PROGRAM_ID: PublicKey = new PublicKey(
  chainEnv(MORFI_CP_SWAP_PROGRAM_ID_ENV, 'morfijP38RdW7M9jJ4rAEpcsyXSQgHpCafN3vgvYwGC'),
);

/**
 * Morfi protocol-owned cp-swap create-pool-fee receiver required by its
 * `initialize` account constraints. A WSOL token account owned (token-authority)
 * by the cp-swap admin. Source of truth:
 * `contracts/morfi-cp-swap/programs/cp-swap/src/lib.rs` create_pool_fee_reveiver::ID.
 */
export const CREATE_POOL_FEE_RECEIVER: PublicKey = new PublicKey(
  'HheDAEhcbU2K224WRWJhBf8cgvzSrdtXZjhAMb16SUm7',
);

/** SPL Token program ID (for USDC). */
export const TOKEN_PROGRAM_ID: PublicKey = new PublicKey(
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
);

/** Token-2022 program ID (for outcome tokens). */
export const TOKEN_2022_PROGRAM_ID: PublicKey = new PublicKey(
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
);

/** Associated Token Account program ID. */
export const ASSOCIATED_TOKEN_PROGRAM_ID: PublicKey = new PublicKey(
  'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
);

/**
 * Quote mint — `MORFI_QUOTE_MINT`, defaulting to the devnet USDC mint
 * (Morfi-controlled, mint authority: deployer wallet).
 */
export let DEVNET_USDC_MINT: PublicKey = new PublicKey(
  chainEnv(MORFI_QUOTE_MINT_ENV, 'GwMYubfhtHzP2vNcoDVRbt64T5ruHRpq5w2wCXGMNTB7'),
);

/**
 * Whether the Markets deployment is the experimental sell-cap program
 * (`idl/markets.sell-cap.json`) — `MORFI_SELL_CAP_PROGRAM=true`, default off.
 * Its buy/sell/create instructions take extra sell-cap accounts, so builders
 * and relay layouts must match the deployment exactly.
 */
export let SELL_CAP_PROGRAM: boolean = parseSellCapProgramFlag(
  chainEnv(MORFI_SELL_CAP_PROGRAM_ENV, 'false'),
);

/**
 * Strict `MORFI_SELL_CAP_PROGRAM` parser: only `true` / `false` (empty = off).
 * Anything else throws, so a typo such as `TRUE` or `1` cannot silently select
 * the classic program and build transactions the deployment rejects.
 */
export function parseSellCapProgramFlag(raw: string | undefined): boolean {
  const value = raw?.trim() ?? '';
  if (value === 'true') return true;
  if (value === 'false' || value === '') return false;
  throw new Error(`${MORFI_SELL_CAP_PROGRAM_ENV} must be "true" or "false", got "${value}"`);
}

export interface ChainAddresses {
  marketsProgramId?: PublicKey | string;
  cpSwapProgramId?: PublicKey | string;
  quoteMint?: PublicKey | string;
  sellCapProgram?: boolean;
}

/**
 * The chain addresses as resolved at module load, before any
 * `configureChainAddresses` call. Hosts that switch between networks fall
 * back to these, never to the live bindings, which may still hold the
 * previously bound network.
 */
export const DEFAULT_CHAIN_ADDRESSES: Readonly<{
  marketsProgramId: PublicKey;
  cpSwapProgramId: PublicKey;
  quoteMint: PublicKey;
  sellCapProgram: boolean;
}> = Object.freeze({
  marketsProgramId: MORFI_OPINION_PROGRAM_ID,
  cpSwapProgramId: CP_SWAP_PROGRAM_ID,
  quoteMint: DEVNET_USDC_MINT,
  sellCapProgram: SELL_CAP_PROGRAM,
});

/**
 * Repoint the chain addresses above at another deployment. They are live
 * bindings, so every importer — and every helper whose `programId` / `usdMint`
 * parameter defaults to them — sees the new value on its next call. Omitted
 * fields return to `DEFAULT_CHAIN_ADDRESSES`, so one network's addresses never
 * leak into the next; an invalid address throws before anything changes.
 */
export function configureChainAddresses(addresses: ChainAddresses): void {
  const marketsProgramId = toPublicKey(
    addresses.marketsProgramId,
    DEFAULT_CHAIN_ADDRESSES.marketsProgramId,
  );
  const cpSwapProgramId = toPublicKey(
    addresses.cpSwapProgramId,
    DEFAULT_CHAIN_ADDRESSES.cpSwapProgramId,
  );
  const quoteMint = toPublicKey(addresses.quoteMint, DEFAULT_CHAIN_ADDRESSES.quoteMint);
  MORFI_OPINION_PROGRAM_ID = marketsProgramId;
  CP_SWAP_PROGRAM_ID = cpSwapProgramId;
  DEVNET_USDC_MINT = quoteMint;
  SELL_CAP_PROGRAM = addresses.sellCapProgram ?? DEFAULT_CHAIN_ADDRESSES.sellCapProgram;
}

function toPublicKey(value: PublicKey | string | undefined, fallback: PublicKey): PublicKey {
  if (value === undefined) return fallback;
  return typeof value === 'string' ? new PublicKey(value) : value;
}

/** System program ID. */
export const SYSTEM_PROGRAM_ID: PublicKey = new PublicKey('11111111111111111111111111111111');
